import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Subtitle from 'LuxKit/Typography/Subtitle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { LUX_BUNDLE_LABEL } from 'constants/productLabel'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import OfferDescription from '../OfferDescription'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  width: ${rem(80)};
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-left: ${rem(8)};
  }
`

interface Props {
  offer: App.Offer;
  locationCaption: string;
  bundledOffers: Array<App.BundledOffer>;
}

function BundleAndSavePropertyDetails(props: Props) {
  const {
    offer,
    locationCaption,
    bundledOffers,
  } = props

  const offerPropertiesTitle = useMemo(
    () => offer.location || bundledOffers.map(offer => offer.property.name).join(' + '),
    [offer.location, bundledOffers])

  return (<>
    <Header>
      <Group direction="vertical" gap={8}>
        <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />} data-testid="offer-location">
          {locationCaption}
        </BodyTextBlock>
        <Heading variant="heading2" as="h1">
          <Heading variant="heading2" as="b" colour="highlight-secondary">{LUX_BUNDLE_LABEL}:</Heading>{' '}
          {offerPropertiesTitle}
        </Heading>
        <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>
      </Group>
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" verticalAlign="center" gap={16}>
          <PropertyLogo
            dpr={2}
            enhancedFormats={false}
            id={bundledOffers[0].property.logoImageId}
            alt={`${bundledOffers[0].property.name || 'Vendor'} logo`}
          />
          <LinePlusIcon />
          <PropertyLogo
            dpr={2}
            enhancedFormats={false}
            id={bundledOffers[1].property.logoImageId}
            alt={`${bundledOffers[1].property.name || 'Vendor'} logo`}
          />
        </Group>
      </CSSBreakpoint>
    </Header>
    {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
      {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
    </OfferBadgeTagSection>}
    {offer.description && <OfferDescription description={offer.description} />}
  </>)
}

export default BundleAndSavePropertyDetails
